/**
 * deriveBillingView — pure view model for the /account/billing center.
 *
 * Turns the catalog + the user's subscription state into one row per visible
 * product, with the current plan and the set of available actions. No I/O, no
 * Stripe, no money math beyond reading catalog prices — all charge/credit
 * amounts come from the server preview (golden rule: never compute credits
 * client-side). Mirrors the style of derivePlanView.ts.
 *
 * See docs/design/account-billing-center-DESIGN.md §1.5.
 */

import type {
  BillingInterval,
  CatalogData,
  CatalogProduct,
  ProductTier,
  ScheduledChange,
  SubscriptionState,
} from "@d-m-organization/pricing-ui";

export type BillingStatus =
  | "free"
  | "active"
  | "trialing"
  | "past_due"
  | "canceled";

export type BillingRow = {
  readonly productId: string;
  readonly displayName: string;
  readonly status: BillingStatus;
  readonly tier: ProductTier;
  readonly interval: BillingInterval | null;
  readonly currentPeriodEnd: number | null;
  readonly cancelAtPeriodEnd: boolean;
  /** Source of the entitlement — "bundle" rows are managed by the ecosystem sub. */
  readonly viaBundle: boolean;
  /**
   * ADR-0008: this row is an admin "complimentary" grant (no Stripe sub).
   * Rendered with a "Complimentary" badge instead of the inert "Free" status.
   */
  readonly isComp: boolean;
  /** Comp end (epoch ms); null for a permanent comp or non-comp row. */
  readonly compExpiresAt: number | null;
  /** Price of the current (tier, interval) in cents, or null for free/unknown. */
  readonly priceCents: number | null;
  readonly scheduledChange: ScheduledChange | null;
  readonly canUpgradeTier: boolean;
  readonly canDowngradeTier: boolean;
  readonly canSwitchToYear: boolean;
  readonly canSwitchToMonth: boolean;
  readonly isBuyable: boolean;
};

const TIER_RANK: Record<ProductTier, number> = { free: 0, pro: 1, business: 2 };
const PAID_TIERS: readonly ProductTier[] = ["pro", "business"];

function mapStatus(s: string | undefined): BillingStatus {
  switch (s) {
    case "active":
      return "active";
    case "trialing":
      return "trialing";
    case "past_due":
    case "unpaid":
      return "past_due";
    case "canceled":
      return "canceled";
    default:
      return "free";
  }
}

function tierPriceCents(
  product: CatalogProduct,
  tier: ProductTier,
  interval: BillingInterval,
): number | null {
  const info = product.tiers[tier];
  if (!info) return null;
  return interval === "year" ? info.annualPriceCents : info.monthlyPriceCents;
}

function availablePaidTiers(product: CatalogProduct): ProductTier[] {
  return PAID_TIERS.filter((t) => product.tiers[t]?.active);
}

function hasActivePrice(
  product: CatalogProduct,
  tier: ProductTier,
  interval: BillingInterval,
): boolean {
  const info = product.tiers[tier];
  if (!info || !info.active) return false;
  const cents = tierPriceCents(product, tier, interval);
  return typeof cents === "number" && cents > 0;
}

function buildRow(
  product: CatalogProduct,
  sub: SubscriptionState | null,
): BillingRow {
  const ent = sub?.entitlements?.[product.productId];
  const individual = sub?.products?.find(
    (p) => p.productId === product.productId,
  );
  const ecosystem = sub?.ecosystem ?? null;
  const viaBundle =
    ent?.source === "bundle" ||
    (!!ecosystem &&
      (ecosystem.status === "active" || ecosystem.status === "trialing") &&
      ecosystem.products.includes(product.productId));

  const tier: ProductTier = ent?.tier ?? "free";
  // ADR-0008: an admin comp resolves to a paid tier with no Stripe sub. It is
  // outranked by a real sub (bundle/individual), so only treat it as a comp
  // when neither of those covers the product.
  const isComp = ent?.source === "admin" && !viaBundle && !individual;
  const compExpiresAt = isComp ? (ent?.expiresAt ?? null) : null;
  const status: BillingStatus = viaBundle
    ? mapStatus(ecosystem?.status)
    : individual
      ? mapStatus(individual.status)
      : "free";

  const interval: BillingInterval | null = viaBundle
    ? (ecosystem?.interval ?? null)
    : (individual?.interval ?? null);

  const scheduledChange = individual?.scheduledChange ?? null;
  const currentPeriodEnd = viaBundle
    ? (ecosystem?.currentPeriodEnd ?? null)
    : (individual?.currentPeriodEnd ?? null);
  const cancelAtPeriodEnd = viaBundle ? false : (individual?.cancelAtPeriodEnd ?? false);

  const priceCents =
    tier === "free" ? null : tierPriceCents(product, tier, interval ?? "month");

  // Plan changes are only offered on an active/trialing INDIVIDUAL sub, with no
  // pending scheduled change and not while past_due. Bundle-covered products
  // are managed through the bundle, not per-product.
  const changeable =
    !viaBundle &&
    (status === "active" || status === "trialing") &&
    !scheduledChange;

  const paid = availablePaidTiers(product);
  const rank = TIER_RANK[tier];

  const canUpgradeTier =
    changeable && paid.some((t) => TIER_RANK[t] > rank);
  // Downgrade only to a lower PAID tier (dropping to free is a Portal cancel).
  const canDowngradeTier =
    changeable && paid.some((t) => TIER_RANK[t] < rank && TIER_RANK[t] > 0);

  const canSwitchToYear =
    changeable && interval === "month" && hasActivePrice(product, tier, "year");
  const canSwitchToMonth =
    changeable && interval === "year" && hasActivePrice(product, tier, "month");

  // Buyable when there is no active paid plan yet and the product sells a paid tier.
  const isBuyable =
    (status === "free" || status === "canceled") &&
    !viaBundle &&
    paid.length > 0;

  return {
    productId: product.productId,
    displayName: product.displayName,
    status,
    tier,
    interval,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    viaBundle,
    isComp,
    compExpiresAt,
    priceCents,
    scheduledChange,
    canUpgradeTier,
    canDowngradeTier,
    canSwitchToYear,
    canSwitchToMonth,
    isBuyable,
  };
}

// Attention order: past_due first, then active/trialing, then canceled, then free.
const STATUS_ORDER: Record<BillingStatus, number> = {
  past_due: 0,
  active: 1,
  trialing: 1,
  canceled: 2,
  free: 3,
};

export function deriveBillingView(
  catalog: CatalogData | null,
  subscription: SubscriptionState | null,
): BillingRow[] {
  if (!catalog) return [];
  const visible = catalog.products.filter((p) => p.visible);
  const rows = visible.map((p) => buildRow(p, subscription));
  return rows
    .map((row, i) => ({ row, i }))
    .sort((a, b) => {
      const s = STATUS_ORDER[a.row.status] - STATUS_ORDER[b.row.status];
      return s !== 0 ? s : a.i - b.i; // stable: preserve catalog order within a group
    })
    .map(({ row }) => row);
}


// ---------------------------------------------------------------------------
// Deep-link parsing — /account/billing?product=<id>&action=<a>
// ---------------------------------------------------------------------------

export type BillingDeepLinkAction = "buy" | "change" | "switch-month" | "switch-year";
const VALID_ACTIONS: readonly BillingDeepLinkAction[] = ["buy", "change", "switch-month", "switch-year"];

/**
 * Parse a /account/billing deep link (e.g. from the pricing page or a Dashboard
 * upsell). Returns the requested product + action, or null if absent/invalid.
 * Pure — the caller decides whether the action is actually applicable.
 */
export function parseBillingDeepLink(
  search: string,
): { product: string; action: BillingDeepLinkAction } | null {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return null;
  }
  const product = params.get("product");
  const action = params.get("action");
  if (!product || !action) return null;
  if (!(VALID_ACTIONS as readonly string[]).includes(action)) return null;
  return { product, action: action as BillingDeepLinkAction };
}
